import { useEffect, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { useProductSizesForRecipe } from "../hooks/useRecipes";
import type { Recipe } from "../models/Recipe";

interface AddRecipeModalProps {
	open: boolean;
	existingRecipes: Recipe[];
	onSave: (productSizeId: number, name: string) => void;
	onCancel: () => void;
	isSaving: boolean;
}

export const AddRecipeModal = ({
	open,
	existingRecipes,
	onSave,
	onCancel,
	isSaving,
}: AddRecipeModalProps) => {
	const [productSizeId, setProductSizeId] = useState<number | null>(null);
	const [name, setName] = useState("");
	const [includeInactive, setIncludeInactive] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	// By default the dropdown lists only active products; tick to also include
	// inactive ones (so a recipe can be created for an inactive product).
	const { data: productSizes = [] } = useProductSizesForRecipe(includeInactive);

	useEffect(() => {
		if (open) {
			setProductSizeId(null);
			setName("");
			setIncludeInactive(false);
			setErrors({});
		}
	}, [open]);

	const nameTaken =
		!!productSizeId &&
		existingRecipes.some(
			(r) =>
				r.ProductSizeId === productSizeId &&
				r.Name.trim().toLowerCase() === name.trim().toLowerCase(),
		);

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};
		if (!productSizeId) {
			newErrors.ProductSizeId = "Product is required";
		}
		if (!name.trim()) {
			newErrors.Name = "Name is required";
		} else if (nameTaken) {
			newErrors.Name = "This product already has a recipe with this name";
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const canSave = !!productSizeId && !!name.trim() && !nameTaken;

	const handleSubmit = () => {
		if (!validate()) return;
		if (!productSizeId) return;
		onSave(productSizeId, name.trim());
	};

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontWeight: 600 }}>ADD RECIPE</DialogTitle>
			<DialogContent>
				<Grid2 container spacing={1.5} sx={{ mt: 0.5 }}>
					<Grid2 size={{ xs: 12 }}>
						<Autocomplete
							options={productSizes}
							getOptionLabel={(ps) => `(${ps.ProductTypeName}) ${ps.Name}`}
							isOptionEqualToValue={(o, v) => o.Id === v.Id}
							value={productSizes.find((ps) => ps.Id === productSizeId) ?? null}
							onChange={(_e, opt) => setProductSizeId(opt?.Id ?? null)}
							size="small"
							fullWidth
							renderInput={(params) => (
								<TextField
									{...params}
									label="Product"
									required
									error={!!errors.ProductSizeId}
									helperText={errors.ProductSizeId}
								/>
							)}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={includeInactive}
										onChange={(e) => setIncludeInactive(e.target.checked)}
									/>
								}
								label="Include inactive products"
								slotProps={{ typography: { variant: "body2" } }}
							/>
							<Typography variant="body2" color="text.secondary">
								{productSizes.length} product(s)
							</Typography>
						</Box>
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							label="Name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							error={!!errors.Name || nameTaken}
							helperText={
								errors.Name ||
								(nameTaken
									? "This product already has a recipe with this name"
									: "")
							}
							fullWidth
							required
							size="small"
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
				</Grid2>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					onClick={onCancel}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CANCEL
				</Button>
				<Button
					color="primary"
					variant="contained"
					onClick={handleSubmit}
					disabled={!canSave || isSaving}
				>
					{isSaving ? "CREATING..." : "CREATE"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
